import {
  Component,
  ViewChild,
  forwardRef,
  Output,
  OnInit,
  EventEmitter,
  AfterViewInit
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import {
  NgSignaturePadOptions,
  SignaturePadComponent
} from '@almothafar/angular-signature-pad';
import { WorkflowContextService } from 'src/app/services/workflow-context.service';
import { LayoutService } from 'src/app/services/layout.service';

@Component({
    selector: 'wm-signature-field',
    templateUrl: 'signature-field.component.html',
    styleUrls: ['signature-field.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SignatureFieldComponent),
            multi: true
        }
    ],
    standalone: false
})
export class SignatureFieldComponent
  implements ControlValueAccessor, OnInit, AfterViewInit {

  @ViewChild('signaturePad', { static: false })
  signaturePad!: SignaturePadComponent;

  @Output() valueChange = new EventEmitter<string>();

  private _value: string = '';
  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};
  private pendingData: any[] | null = null;
  isDrawing = false;
  templateForPermitForm:boolean=false;

  public signaturePadOptions: NgSignaturePadOptions = {
    // passed through to szimek/signature_pad constructor
    backgroundColor: 'rgba(0, 0, 0, 0)',
    minWidth: 0.5,
    canvasWidth: 500,
    canvasHeight: 145
  };

  writeValue(value: string | null): void {
    this._value = value ?? '';

    if (this._value) {
      try {
        const parsed = JSON.parse(this._value);
        if (parsed?.data) {
          this.pendingData = parsed.data;
          this.restoreIfReady();
        }
      } catch {}
    } else {
      this.clearPad();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngAfterViewInit(): void {
    this.restoreIfReady();
  }

  private restoreIfReady(): void {
    if (this.signaturePad && this.pendingData) {
      this.signaturePad.fromData(this.pendingData);
      this.pendingData = null;
    }
  }

  drawBegin(): void {
    this.isDrawing = true;
    // Added on 17-04-2026 by amisha to mark the custom signature control as interacted with,
    // so the required red border shows only after user interaction on permit forms.
    this.onTouched();
  }

  drawComplete(): void {
    this.isDrawing = false;
    const value = JSON.stringify({
      image: this.signaturePad.toDataURL('image/png', 0.5),
      data: this.signaturePad.toData()
    });

    this._value = value;
    // Added on 17-04-2026 bya amisha to keep Angular touched state in sync for validation styling
    // on the permit-form signature field after drawing is completed.
    this.onTouched();
    this.onChange(value);
    this.valueChange.emit(value);
  }

  clear(): void {
    this.clearPad();
    this._value = '';
    // Added on 17-04-2026 by amisha so clearing the signature also updates touched state,
    // allowing required validation styling to behave consistently on permit forms.
    this.onTouched();
    this.onChange('');
    this.valueChange.emit('');
  }

  private clearPad(): void {
    if (this.signaturePad) {
      this.signaturePad.clear();
    }
  }

  ngOnInit(): void {}

  constructor(public layoutService: LayoutService, context : WorkflowContextService) {
      layoutService.useNewLayout$.subscribe((useNewLayout) => { 
      this.templateForPermitForm = useNewLayout;
    });
  }
}
